import { useInfiniteQuery, InfiniteData, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect } from 'react';

import { MembersContext } from '@/contexts/membersContext';
import PaginatedResponse from '@/types/paginatedResponse';
import RepositoryResponse from '@/types/repositoryResponse';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

interface ApiPaginatedInfiniteQuery<
  TData,
  TError = Error,
  TSelectData = InfiniteData<RepositoryResponse<PaginatedResponse<TData>>>
> extends Omit<
  UseInfiniteQueryOptions<
    RepositoryResponse<PaginatedResponse<TData>>,
    TError,
    TSelectData,
    unknown[],
    number
  >,
  'getNextPageParam' | 'getPreviousPageParam' | 'initialPageParam'
> {
  queryFn: (params: { pageParam: number }) => Promise<RepositoryResponse<PaginatedResponse<TData>>>;
  initialPageParam?: number;
}

const useAPIPaginated = <
  TData,
  TError = Error,
  TSelectData = InfiniteData<RepositoryResponse<PaginatedResponse<TData>>>
>({
  queryFn,
  enabled = true,
  refetchOnWindowFocus = false,
  initialPageParam = 1,
  ...restOptions
}: ApiPaginatedInfiniteQuery<TData, TError, TSelectData>) => {
  const { addMessage } = useContext(MembersContext);

  const queryResponse = useInfiniteQuery({
    ...restOptions,
    enabled,
    queryFn,
    initialPageParam,
    getNextPageParam: (lastPage: RepositoryResponse<PaginatedResponse<TData>>) => {
      if (!lastPage.data?.links?.next) {
        return undefined;
      }
      return lastPage.data.links.next;
    },
    getPreviousPageParam: (firstPage: RepositoryResponse<PaginatedResponse<TData>>) => {
      if (!firstPage.data?.links?.previous) {
        return undefined;
      }
      return firstPage.data.links.previous;
    },
    refetchOnWindowFocus,
    retry: false
  });

  const { error, isError } = queryResponse;

  useEffect(() => {
    if (isError) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  }, [addMessage, error, isError]);

  return queryResponse;
};

export default useAPIPaginated;
